import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { formatImageUrl } from '../utils/formatImageUrl';
import { useGlobal } from '../context/GlobalContext';

const STAGES = [
    { id: 1, key: 'placed', label: 'Order Placed', icon: 'ph-shopping-bag', desc: 'Order details received' },
    { id: 2, key: 'payment', label: 'Payment Confirmed', icon: 'ph-credit-card', desc: 'Payment verified securely' },
    { id: 3, key: 'shipped', label: 'Shipped', icon: 'ph-package', desc: 'Handed over to courier' },
    { id: 4, key: 'out_for_delivery', label: 'Out for Delivery', icon: 'ph-truck', desc: 'With local delivery agent' },
    { id: 5, key: 'delivered', label: 'Delivered', icon: 'ph-house-line', desc: 'Delivered to your doorstep' },
];

const OrderConfirmation = () => {
    const { id } = useParams();
    const location = useLocation();
    const { fetchCart } = useGlobal();

    const [order, setOrder] = useState(location.state?.order || null);
    const [loading, setLoading] = useState(!location.state?.order);
    const [error, setError] = useState(null);

    // Refresh cart badge after order placement
    useEffect(() => {
        if (fetchCart) fetchCart();
    }, [fetchCart]);

    const fetchOrderDetails = useCallback(async () => {
        const orderIdToFetch = id || location.state?.orderId;
        if (!orderIdToFetch) {
            try {
                const res = await api.get('/api/orders');
                if (Array.isArray(res.data) && res.data.length > 0) {
                    setOrder(res.data[0]);
                } else {
                    setError('No recent orders found.');
                }
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to load order details');
            } finally {
                setLoading(false);
            }
            return;
        }

        try {
            setLoading(true);
            const res = await api.get('/api/orders/' + orderIdToFetch);
            setOrder(res.data);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch order:', err);
            setError(err.response?.data?.message || 'Could not find order details. Please check My Orders.');
        } finally {
            setLoading(false);
        }
    }, [id, location.state]);

    useEffect(() => {
        if (!order || id) {
            fetchOrderDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, fetchOrderDetails]);

    const getActiveStageNumber = (orderData) => {
        if (!orderData) return 1;
        const status = (orderData.status || '').toLowerCase();
        const isCancelled = status === 'cancelled';
        if (isCancelled) return -1;

        if (status === 'delivered') return 5;
        if (status === 'processing' || status === 'delivery') return 4;
        if (status === 'shipped') return 3;
        if (status === 'confirmed') return 2;

        const isPrepaid = (orderData.paymentMethod || '').toLowerCase() !== 'cod' && (orderData.paymentType || '').toUpperCase() !== 'FULL_COD';
        const isPaid = (orderData.paymentStatus || '').toLowerCase() === 'completed' || (orderData.paymentStatus || '').toLowerCase() === 'paid';
        if (isPaid || isPrepaid) return 2;

        return 1;
    };

    const isOrderCancellable = (orderData) => {
        if (!orderData || !orderData.createdAt) return false;
        const status = (orderData.status || '').toLowerCase();
        if (!['pending', 'confirmed'].includes(status)) return false;
        const orderTime = new Date(orderData.createdAt).getTime();
        if (isNaN(orderTime)) return false;
        const hours = (Date.now() - orderTime) / (1000 * 60 * 60);
        return hours >= 0 && hours <= 2;
    };

    const getCancellationTimeRemaining = (orderData) => {
        if (!orderData || !orderData.createdAt) return null;
        const orderTime = new Date(orderData.createdAt).getTime();
        if (isNaN(orderTime)) return null;
        const elapsedMs = Date.now() - orderTime;
        const remainingMs = (2 * 60 * 60 * 1000) - elapsedMs;
        if (remainingMs <= 0) return null;
        const hours = Math.floor(remainingMs / (1000 * 60 * 60));
        const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 0) {
            return `${hours}h ${minutes}m left to cancel`;
        }
        return `${minutes}m left to cancel`;
    };

    // Execute Order Cancellation
    const executeCancelOrder = async () => {
        if (!order) return;
        const loadingToast = toast.loading("Cancelling order...");
        try {
            await api.patch(`/api/returns/cancel-order/${order.id}`);
            toast.dismiss(loadingToast);
            toast.success("Order cancelled successfully!");
            fetchOrderDetails();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || "Failed to cancel order");
        }
    };

    // Trigger Toast Confirmation
    const handleCancelOrder = () => {
        if (!order) return;
        const orderRef = order.referenceSlug || order.order_number || order.id;

        toast((t) => (
            <div style={{ minWidth: '250px' }}>
                <div className="d-flex align-items-center gap-8 mb-6">
                    <i className="ph-bold ph-warning-circle text-danger-600" style={{ fontSize: '20px' }} />
                    <span className="fw-bold text-neutral-900" style={{ fontSize: '14px' }}>Cancel Order #{orderRef}?</span>
                </div>
                <p className="text-xs text-gray-600 mb-12">
                    Are you sure you want to cancel this order?
                </p>
                <div className="d-flex align-items-center justify-content-end gap-8">
                    <button
                        type="button"
                        className="py-6 px-12 rounded-6 text-xs fw-semibold"
                        style={{ background: '#F1F5F9', border: '1px solid #CBD5E1', color: '#334155', cursor: 'pointer' }}
                        onClick={() => toast.dismiss(t.id)}
                    >
                        Keep Order
                    </button>
                    <button
                        type="button"
                        className="py-6 px-14 rounded-6 text-xs fw-bold"
                        style={{ background: '#DC2626', border: 'none', color: '#FFFFFF', cursor: 'pointer' }}
                        onClick={() => {
                            toast.dismiss(t.id);
                            executeCancelOrder();
                        }}
                    >
                        Yes, Cancel
                    </button>
                </div>
            </div>
        ), {
            duration: 8000,
            style: {
                borderRadius: '12px',
                background: '#FFFFFF',
                color: '#1E293B',
                boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.2), 0 0 0 1px rgba(15, 23, 42, 0.05)',
                padding: '14px 16px',
            },
        });
    };

    const currentStage = getActiveStageNumber(order);
    const isCancelled = (order?.status || '').toLowerCase() === 'cancelled';
    const canCancel = isOrderCancellable(order);
    const timeRemaining = canCancel ? getCancellationTimeRemaining(order) : null;

    if (loading) {
        return (
            <div className="py-80 text-center container">
                <div className="spinner-border text-main-600 mb-16" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="text-neutral-800">Fetching your order confirmation...</h5>
                <p className="text-gray-500">Please wait while we load your receipt and shipping details.</p>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="py-80 text-center container">
                <div className="flex-center rounded-circle bg-danger-50 text-danger-600 mx-auto mb-20" style={{ width: 80, height: 80 }}>
                    <i className="ph ph-warning-circle" style={{ fontSize: 42 }} />
                </div>
                <h4 className="fw-bold text-neutral-900 mb-12">Unable to Load Order</h4>
                <p className="text-gray-600 mb-28 max-w-500 mx-auto">{error || "We couldn't retrieve the details for this order."}</p>
                <div className="flex-center gap-16 flex-wrap">
                    <Link to="/account?tab=orders" className="btn btn-main py-12 px-28 rounded-8">
                        View All Orders
                    </Link>
                    <Link to="/shop" className="btn btn-outline-main py-12 px-28 rounded-8">
                        Continue Shopping
                    </Link>
                </div>
            </div>
        );
    }

    const items = order.items || [];
    const shippingAddr = order.shippingAddress || {};
    const formattedDate = order.createdAt
        ? new Date(order.createdAt).toLocaleDateString('en-IN', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
        : 'Just now';

    const orderNumber = order.referenceSlug || order.order_number || order.id;
    const isCod = (order.paymentMethod || '').toLowerCase() === 'cod';
    const finalTotal = parseFloat(order.totalAmount || 0);
    const subtotal = parseFloat(order.subtotal || items.reduce((sum, item) => sum + (parseFloat(item.price || 0) * (item.quantity || 1)), 0));
    const shippingCharge = parseFloat(order.shippingCharge || 0);
    const couponDiscount = parseFloat(order.couponDiscount || 0);

    const handlePrint = () => {
        window.print();
    };

    return (
        <section className="order-confirmation-section py-60">
            <style>{`
                .oc-hero-card {
                    background: linear-gradient(135deg, #F0FDF4 0%, #E7F7EC 100%);
                    border: 1px solid #BBF7D0;
                    border-radius: 20px;
                    padding: 28px 16px;
                    text-align: center;
                    margin-bottom: 24px;
                    position: relative;
                    overflow: hidden;
                }
                @media (min-width: 576px) {
                    .oc-hero-card {
                        padding: 40px 24px;
                        margin-bottom: 32px;
                    }
                }
                .oc-check-circle {
                    width: 64px;
                    height: 64px;
                    background: #2FA84F;
                    color: #fff;
                    border-radius: 50%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 32px;
                    margin-bottom: 16px;
                    box-shadow: 0 10px 25px rgba(47, 168, 79, 0.35);
                    animation: scaleUp 0.4s ease-out;
                }
                @media (min-width: 576px) {
                    .oc-check-circle {
                        width: 72px;
                        height: 72px;
                        font-size: 38px;
                        margin-bottom: 20px;
                    }
                }
                @keyframes scaleUp {
                    0% { transform: scale(0.5); opacity: 0; }
                    100% { transform: scale(1); opacity: 1; }
                }
                .oc-card {
                    background: #ffffff;
                    border: 1px solid #EAEAEA;
                    border-radius: 16px;
                    padding: 18px 16px;
                    margin-bottom: 20px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
                }
                @media (min-width: 576px) {
                    .oc-card {
                        padding: 28px 24px;
                        margin-bottom: 24px;
                    }
                }
                .oc-card-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #111827;
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                @media (min-width: 576px) {
                    .oc-card-title {
                        font-size: 18px;
                        margin-bottom: 20px;
                    }
                }
                .stage-tracker-container {
                    padding: 10px 0;
                }
                .stage-stepper {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    position: relative;
                }
                .stage-stepper::before {
                    content: '';
                    position: absolute;
                    top: 24px;
                    left: 40px;
                    right: 40px;
                    height: 4px;
                    background: #E5E7EB;
                    z-index: 1;
                }
                .stage-step {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    flex: 1;
                    padding: 0 8px;
                }
                .stage-icon-wrap {
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    background: #F3F4F6;
                    border: 3px solid #E5E7EB;
                    color: #9CA3AF;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                    margin-bottom: 12px;
                    transition: all 0.3s ease;
                }
                .stage-step.completed .stage-icon-wrap {
                    background: #2FA84F;
                    border-color: #2FA84F;
                    color: #ffffff;
                    box-shadow: 0 4px 12px rgba(47, 168, 79, 0.3);
                }
                .stage-step.current .stage-icon-wrap {
                    background: #ffffff;
                    border-color: #2FA84F;
                    color: #2FA84F;
                    box-shadow: 0 0 0 4px rgba(47, 168, 79, 0.15);
                    animation: pulseRing 2s infinite;
                }
                @keyframes pulseRing {
                    0% { box-shadow: 0 0 0 0 rgba(47, 168, 79, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(47, 168, 79, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(47, 168, 79, 0); }
                }
                .stage-label {
                    font-size: 14px;
                    font-weight: 700;
                    color: #4B5563;
                    margin-bottom: 4px;
                }
                .stage-step.completed .stage-label,
                .stage-step.current .stage-label {
                    color: #111827;
                }
                .stage-desc {
                    font-size: 12px;
                    color: #9CA3AF;
                    line-height: 1.3;
                    max-width: 140px;
                }
                .stage-step.completed .stage-desc,
                .stage-step.current .stage-desc {
                    color: #6B7280;
                }
                @media (max-width: 767px) {
                    .stage-stepper {
                        flex-direction: column;
                        gap: 16px;
                    }
                    .stage-stepper::before {
                        top: 20px;
                        bottom: 20px;
                        left: 20px;
                        right: auto;
                        width: 2px;
                        height: auto;
                    }
                    .stage-step {
                        flex-direction: row;
                        align-items: center;
                        text-align: left;
                        gap: 14px;
                        width: 100%;
                        padding: 0;
                    }
                    .stage-icon-wrap {
                        margin-bottom: 0;
                        flex-shrink: 0;
                        width: 42px;
                        height: 42px;
                        font-size: 18px;
                    }
                    .stage-desc {
                        max-width: 100%;
                    }
                }
                .oc-item-row {
                    display: flex;
                    align-items: flex-start;
                    gap: 14px;
                    padding: 16px 0;
                    border-bottom: 1px solid #F3F4F6;
                }
                .oc-item-row:last-child {
                    border-bottom: none;
                    padding-bottom: 0;
                }
                .oc-item-row:first-child {
                    padding-top: 0;
                }
                .oc-item-thumb {
                    width: 72px;
                    height: 72px;
                    border-radius: 10px;
                    object-fit: contain;
                    background: #F9FAFB;
                    border: 1px solid #E5E7EB;
                    flex-shrink: 0;
                }
                .oc-item-info {
                    flex: 1 1 0%;
                    min-width: 0;
                }
                .oc-item-meta {
                    text-align: right;
                    flex-shrink: 0;
                }
                @media (max-width: 575px) {
                    .oc-item-row {
                        flex-wrap: wrap;
                        gap: 12px;
                        padding: 14px 0;
                    }
                    .oc-item-thumb {
                        width: 64px;
                        height: 64px;
                    }
                    .oc-item-info {
                        flex: 1 1 calc(100% - 78px);
                        min-width: 0;
                    }
                    .oc-item-meta {
                        width: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: flex-end;
                        padding-top: 8px;
                        margin-top: 4px;
                        border-top: 1px dashed #F3F4F6;
                        text-align: right;
                    }
                }
                .oc-info-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                }
                .oc-info-block {
                    background: #F9FAFB;
                    border-radius: 12px;
                    padding: 18px;
                    border: 1px solid #F3F4F6;
                }
                .oc-info-label {
                    font-size: 12px;
                    text-transform: uppercase;
                    color: #6B7280;
                    font-weight: 700;
                    margin-bottom: 8px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .oc-info-val {
                    font-size: 14px;
                    color: #111827;
                    font-weight: 500;
                    line-height: 1.5;
                }
                .oc-actions-bar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 16px;
                    padding-top: 20px;
                }
                .btn-cancel-order {
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    gap: 6px !important;
                    padding: 10px 20px !important;
                    border-radius: 8px !important;
                    font-size: 13px !important;
                    font-weight: 700 !important;
                    line-height: 1 !important;
                    cursor: pointer !important;
                    transition: all 0.2s ease !important;
                    border: 1.5px solid #EF4444 !important;
                    background-color: #FEF2F2 !important;
                    color: #DC2626 !important;
                    text-decoration: none !important;
                }
                .btn-cancel-order:hover {
                    background-color: #DC2626 !important;
                    border-color: #DC2626 !important;
                    color: #FFFFFF !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 2px 8px rgba(220, 38, 38, 0.25) !important;
                }
                .btn-cancel-order i {
                    color: inherit !important;
                    font-size: 16px !important;
                }
                @media print {
                    header, footer, .breadcrumb, .shipping, .oc-actions-bar, .bottom-footer {
                        display: none !important;
                    }
                    .oc-hero-card {
                        background: none !important;
                        border: 1px solid #ccc !important;
                        padding: 20px !important;
                    }
                    .oc-card {
                        box-shadow: none !important;
                        border: 1px solid #ccc !important;
                        page-break-inside: avoid;
                    }
                    body {
                        background: #fff !important;
                    }
                }
            `}</style>

            <div className="container container-lg">
                {/* Hero Card */}
                <div className="oc-hero-card wow zoomIn" data-wow-duration="0.75s">
                    <div className="oc-check-circle">
                        <i className="ph-bold ph-check" />
                    </div>
                    <h2 className="fw-bold text-neutral-900 mb-8" style={{ fontSize: '28px' }}>
                        Thank You! Your Order is Placed.
                    </h2>
                    <p className="text-gray-600 mb-16" style={{ fontSize: '15px' }}>
                        We have received your order and sent confirmation details to your registered email & phone.
                    </p>
                    <div className="flex-center gap-12 flex-wrap text-sm text-gray-700">
                        <span className="bg-white py-6 px-16 rounded-pill border fw-bold text-neutral-900">
                            Order #{orderNumber}
                        </span>
                        <span>•</span>
                        <span>Placed on <strong>{formattedDate}</strong></span>
                        <span>•</span>
                        <span className="text-main-600 fw-bold">
                            Total: ₹{finalTotal.toFixed(2)}
                        </span>
                    </div>
                </div>

                {/* 5-Stage Live Order Tracker */}
                <div className="oc-card wow fadeInUp" data-wow-delay="0.2s">
                    <div className="oc-card-title flex-between flex-wrap gap-12">
                        <div className="flex-align gap-10">
                            <i className="ph ph-path text-main-600 text-2xl" />
                            <span>Order Progress & Status</span>
                        </div>
                        {isCancelled ? (
                            <span className="badge bg-danger-50 text-danger-600 px-12 py-6 rounded-pill fw-bold">
                                Order Cancelled
                            </span>
                        ) : (
                            <span className="badge bg-main-50 text-main-600 px-12 py-6 rounded-pill fw-bold">
                                Stage {Math.min(Math.max(currentStage, 1), 5)} of 5: {STAGES[Math.min(Math.max(currentStage - 1, 0), 4)].label}
                            </span>
                        )}
                    </div>

                    {isCancelled ? (
                        <div className="alert alert-danger mb-0 rounded-12 p-16 flex-align gap-12">
                            <i className="ph ph-x-circle text-2xl" />
                            <div>
                                <strong>This order has been cancelled.</strong> If an advance or prepaid amount was collected, a refund has been initiated to the original payment source.
                            </div>
                        </div>
                    ) : (
                        <div className="stage-tracker-container">
                            <div className="stage-stepper">
                                {STAGES.map((stage) => {
                                    const isCompleted = currentStage > stage.id || (currentStage === 5 && stage.id === 5);
                                    const isCurrent = currentStage === stage.id && currentStage < 5;
                                    const stepClass = isCompleted ? 'completed' : (isCurrent ? 'current' : 'pending');

                                    return (
                                        <div key={stage.id} className={`stage-step ${stepClass}`}>
                                            <div className="stage-icon-wrap">
                                                {isCompleted ? (
                                                    <i className="ph-bold ph-check" />
                                                ) : (
                                                    <i className={`ph ${stage.icon}`} />
                                                )}
                                            </div>
                                            <div>
                                                <div className="stage-label">{stage.label}</div>
                                                <div className="stage-desc">{stage.desc}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="row gy-4">
                    {/* Ordered Items */}
                    <div className="col-lg-7">
                        <div className="oc-card">
                            <div className="oc-card-title flex-between">
                                <span>Items Ordered ({items.length})</span>
                                <span className="text-xs text-gray-500 font-normal">Direct from Healthy Homes</span>
                            </div>

                            <div className="oc-items-list">
                                {items.map((item, idx) => {
                                    const itemPrice = parseFloat(item.price || 0);
                                    const itemQty = item.quantity || 1;
                                    const itemTotal = itemPrice * itemQty;

                                    return (
                                        <div className="oc-item-row" key={item.id || idx}>
                                            <img
                                                src={formatImageUrl(item.image)}
                                                alt={item.productName || item.name || 'Product'}
                                                className="oc-item-thumb"
                                                onError={(e) => {
                                                    e.currentTarget.onerror = null;
                                                    e.currentTarget.src = '/assets/images/thumbs/product-img1.png';
                                                }}
                                            />
                                            <div className="oc-item-info">
                                                <h6 className="text-sm fw-bold text-neutral-900 mb-4 text-line-2">
                                                    {item.productName || item.name || item.comboName || 'Healthy Homes Product'}
                                                </h6>
                                                {item.selectedVariantName && (
                                                    <span className="text-xs text-gray-500 d-block mb-4">
                                                        Variant: <strong>{item.selectedVariantName}</strong>
                                                    </span>
                                                )}
                                                {(item.selectedProductColor || item.selectedProductSize) && (
                                                    <span className="text-xs text-gray-500 d-block mb-4">
                                                        {item.selectedProductColor && <span>Color: {item.selectedProductColor} </span>}
                                                        {item.selectedProductSize && <span>Size: {item.selectedProductSize}</span>}
                                                    </span>
                                                )}
                                                <div className="flex-align gap-8 text-xs text-gray-500">
                                                    <span>Qty: <strong>{itemQty}</strong></span>
                                                    <span>×</span>
                                                    <span>₹{itemPrice.toFixed(2)}</span>
                                                </div>
                                            </div>
                                            <div className="oc-item-meta">
                                                <span className="fw-bold text-neutral-900 text-md">
                                                    ₹{itemTotal.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mt-24 p-16 rounded-12 flex-align gap-14" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                                <div className="flex-center rounded-circle bg-white text-main-600 shadow-sm flex-shrink-0" style={{ width: 44, height: 44 }}>
                                    <i className="ph ph-truck text-2xl" />
                                </div>
                                <div className="flex-grow-1">
                                    <div className="text-sm fw-bold text-neutral-900">
                                        Logistics: {order.courier || order.courierName || 'Shiprocket Express'}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-2">
                                        {order.estimatedDeliveryDays ? (
                                            <span>Est. Delivery: <strong>Within {order.estimatedDeliveryDays} business days</strong></span>
                                        ) : (
                                            <span>Standard delivery in 3-5 business days</span>
                                        )}
                                        {order.awbCode && (
                                            <span className="ms-10">• AWB: <strong>{order.awbCode}</strong></span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="col-lg-5">
                        <div className="oc-card mb-24">
                            <div className="oc-card-title">
                                <i className="ph ph-receipt text-main-600" />
                                <span>Order Summary</span>
                            </div>

                            <div className="d-flex justify-content-between mb-12 text-sm">
                                <span className="text-gray-600">Items Subtotal</span>
                                <span className="fw-semibold text-neutral-900">₹{subtotal.toFixed(2)}</span>
                            </div>

                            {couponDiscount > 0 && (
                                <div className="d-flex justify-content-between mb-12 text-sm text-success">
                                    <span>
                                        Coupon Discount {order.couponCode && <span className="badge bg-success-50 text-success-700 ms-4">{order.couponCode}</span>}
                                    </span>
                                    <span className="fw-bold">-₹{couponDiscount.toFixed(2)}</span>
                                </div>
                            )}

                            <div className="d-flex justify-content-between mb-12 text-sm">
                                <span className="text-gray-600">Shipping Charges</span>
                                <span className="fw-semibold text-neutral-900">
                                    {shippingCharge === 0 ? <span className="text-success fw-bold">FREE</span> : `₹${shippingCharge.toFixed(2)}`}
                                </span>
                            </div>

                            {parseFloat(order.taxAmount || order.gstAmount || 0) > 0 && (
                                <div className="d-flex justify-content-between mb-12 text-sm">
                                    <span className="text-gray-600">GST / Tax</span>
                                    <span className="fw-semibold text-neutral-900">₹{parseFloat(order.taxAmount || order.gstAmount).toFixed(2)}</span>
                                </div>
                            )}

                            <div className="d-flex justify-content-between pt-16 mt-16 border-top text-lg fw-bold text-neutral-900">
                                <span>Total Amount</span>
                                <span className="text-main-600">₹{finalTotal.toFixed(2)}</span>
                            </div>

                            {order.paymentSummary && order.paymentSummary.value > 0 && (
                                <div className="mt-12 p-10 rounded-8 bg-warning-50 text-warning-800 text-xs flex-between">
                                    <span>{order.paymentSummary.label}:</span>
                                    <strong>₹{parseFloat(order.paymentSummary.value).toFixed(2)}</strong>
                                </div>
                            )}
                        </div>

                        <div className="oc-card">
                            <div className="oc-info-grid">
                                <div className="oc-info-block">
                                    <div className="oc-info-label">
                                        <i className="ph ph-credit-card text-main-600" /> Payment Info
                                    </div>
                                    <div className="oc-info-val">
                                        <div className="fw-bold">
                                            {isCod ? 'Cash on Delivery (COD)' : (order.paymentMethod === 'upi' ? 'Online Payment (UPI/Cards)' : (order.paymentMethod || 'Prepaid Online'))}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-4">
                                            Status:{' '}
                                            <span className={`fw-bold ${isCod ? 'text-warning-600' : 'text-success-600'}`}>
                                                {isCod ? 'Pending at Delivery' : 'Paid & Confirmed'}
                                            </span>
                                        </div>
                                        {order.razorpayPaymentId && (
                                            <div className="text-xs text-gray-400 mt-2">
                                                Txn ID: {order.razorpayPaymentId}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="oc-info-block">
                                    <div className="oc-info-label">
                                        <i className="ph ph-map-pin text-main-600" /> Delivery Address
                                    </div>
                                    <div className="oc-info-val">
                                        <div className="fw-bold">{shippingAddr.fullName || 'Customer'}</div>
                                        <div className="text-xs text-gray-600 mt-4">
                                            {shippingAddr.street}
                                            {shippingAddr.apartment ? `, ${shippingAddr.apartment}` : ''}
                                            <br />
                                            {shippingAddr.city}, {shippingAddr.state} - {shippingAddr.pincode}
                                        </div>
                                        {(shippingAddr.phone || shippingAddr.mobile) && (
                                            <div className="text-xs text-gray-500 mt-4 flex-align gap-4">
                                                <i className="ph ph-phone" /> {shippingAddr.phone || shippingAddr.mobile}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="oc-actions-bar mt-20">
                    <div className="flex-align gap-12 flex-wrap">
                        <Link to="/shop" className="btn btn-main py-14 px-32 rounded-8 font-heading fw-bold">
                            <i className="ph ph-shopping-bag me-6" /> Continue Shopping
                        </Link>
                        <Link to="/account?tab=orders" className="btn btn-outline-main py-14 px-28 rounded-8 font-heading fw-bold">
                            <i className="ph ph-user me-6" /> View in My Orders
                        </Link>
                    </div>

                    {canCancel && (
                        <button
                            type="button"
                            onClick={handleCancelOrder}
                            className="btn-cancel-order py-12 px-24 font-heading fw-bold"
                        >
                            <i className="ph-bold ph-x-circle text-lg" /> Cancel Order
                            {timeRemaining && <span className="text-xs opacity-75 ms-4">({timeRemaining})</span>}
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
};

export default OrderConfirmation;
